import { Token } from '@frontend/authentication-v2';
import { Device } from '@frontend/device/types';
import { IoT, IoTProvision, IoTStateName } from '@frontend/iot/types';
import { Logger } from '@frontend/logger';
import { IUser } from '@frontend/user/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface UserData {
    id: string;
    accountId: string;
    auth: Token;
    user?: IUser;
    identification?: string;
}

export interface UserInterfaceNavigationState {
    provision: IoTProvision | null;
    iot: IoT | null;
    iotSettings?: { [key: string]: string };
    device: Device | null;
    user: UserData | null;
    history: string[];
    commandHistory: (() => void)[];
    spot: {
        name: string;
        id: string;
        settings: { [key: string]: string };
    } | null;
}

const initialState: UserInterfaceNavigationState = {
    provision: null,
    iot: null,
    iotSettings: undefined,
    device: null,
    user: null,
    history: [],
    commandHistory: [],
    spot: null
};

const navigationSlice = createSlice({
    name: 'navigation',
    initialState,
    reducers: {
        setProvisioning: (state, action: PayloadAction<IoTProvision | null>) => {
            state.provision = action.payload;
        },
        setDeviceInfo: (state, action: PayloadAction<{ device?: Device | null; iot?: IoT | null } | null>) => {
            if (action.payload == null) {
                state.iot = null;
                state.device = null;
                return;
            }
            if (action.payload.iot !== undefined) state.iot = action.payload.iot;
            if (action.payload.device !== undefined) state.device = action.payload.device;
        },
        startUserSession: (state, action: PayloadAction<{ id: string; accountId: string; auth: Token; user?: IUser }>) => {
            Logger.log('User session started', action.payload);
            state.user = action.payload;
        },
        endUserSession: (state) => {
            Logger.log('User session ended');
            state.user = null;
        },
        addCommandHistory: (state, action: PayloadAction<() => void>) => {
            state.commandHistory.push(action.payload);
        },
        removeCommandHistory: (state) => {
            state.commandHistory.pop();
        },
        clearCommandHistory: (state) => {
            state.commandHistory = [];
        },
        updateIoTState: (state, action: PayloadAction<{ iot_state: IoTStateName }>) => {
            if (state.iot) {
                state.iot.state = action.payload.iot_state;
            }
        },
        setIdentification: (state, action: PayloadAction<string | undefined>) => {
            if (state.user) {
                state.user.identification = action.payload;
            }
        },
        setIotSettings: (state, action: PayloadAction<{ [key: string]: string } | undefined>) => {
            state.iotSettings = action.payload;
        },
        setSpotInfo: (state, action: PayloadAction<{ name: string; id: string; settings: { [key: string]: string } } | null>) => {
            state.spot = action.payload;
        }
    }
});

export const navigationStore = { navigation: navigationSlice.reducer };
export const {
    setProvisioning,
    setDeviceInfo,
    startUserSession,
    endUserSession,
    addCommandHistory,
    removeCommandHistory,
    clearCommandHistory,
    updateIoTState,
    setIdentification,
    setSpotInfo,
    setIotSettings
} = navigationSlice.actions;
